/**
 * Contracts of the documents API — mirrors the backend's `models/detection.py` and
 * `models/documents.py` (see `docs/api/openapi.json`). Field names stay snake_case because they
 * are the wire format.
 */

/**
 * Detection category key, the "profile" the user sees. One of the eight system keys
 * (`PERSONAL`, `IDENTIFICATION`, …) or an admin-made `CUSTOM_<8 hex>`; the list, colours and labels
 * come from the backend (`GET /catalog`, or the session snapshot in `DocumentModel.categories`).
 */
export type Category = string;

/** Where a finding came from; `declaration` marks a section the document itself declares sensitive. */
export type Source = 'regex' | 'enum' | 'llm' | 'declaration' | 'user';

export type FindingStatus = 'active' | 'excluded';

/** Ordered stages of a detection run. */
export type StepKey = 'parse' | 'regex' | 'enum' | 'llm' | 'declarations' | 'consolidate';

export type StepState = 'pending' | 'running' | 'done' | 'skipped' | 'failed';

export type JobStatus = 'queued' | 'running' | 'done' | 'failed' | 'cancelled';

/** The three export variants and review modes. */
export type ExportMode = 'original' | 'redacted' | 'anonymized';

export const EXPORT_MODES: readonly ExportMode[] = ['original', 'redacted', 'anonymized'];

/** A run that is still going; polling continues while the status is one of these. */
export const ACTIVE_JOB_STATUSES: readonly JobStatus[] = ['queued', 'running'];

/** Rectangle in PDF points, origin in the top-left corner of the displayed page. */
export interface RectModel {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export interface FindingModel {
  id: string;
  page: number;
  /** Character offsets into `PageModel.text`. */
  start: number;
  end: number;
  text: string;
  /** Every category of the item that found it (a finding is hidden only when all of them are off). */
  categories: Category[];
  /** The detection item that found it; `null` for declarations and manual findings. */
  item_id: string | null;
  item_name: string | null;
  /** Internal entity type (`OTHER` for manual findings). */
  entity_type: string;
  sources: Source[];
  /** 0–1, internal only (consolidation tie-break); never displayed. */
  confidence: number;
  /** One rectangle per text line the finding spans. */
  rects: RectModel[];
  /** 1-based line on the page, for the audit trail and the findings list. */
  line: number;
  status: FindingStatus;
  /**
   * Replacement value in the anonymized export; the backend assigns it when a finding is read.
   * Always `null` for a declared finding (`declaration` in `sources`): those are blacked out.
   */
  substitute?: string | null;
  /**
   * The export sets the substitute in its bundled DejaVu Sans because the document's own font lacks
   * one of its glyphs; otherwise in the original font. Both at the original size.
   */
  substitute_font_fallback?: boolean;
  note?: string | null;
}

export interface PageModel {
  /** 1-based. */
  number: number;
  /** Displayed size in PDF points (rotation already applied). */
  width: number;
  height: number;
  rotation: number;
  /** Exact text layer; offsets in findings index into this string. Lines end with `\r\n`. */
  text: string;
  findings: FindingModel[];
}

export interface StepModel {
  key: StepKey;
  /** Czech label from the backend; the UI translates by `key` in other languages. */
  label_cs: string;
  state: StepState;
  count: number;
  started_at?: string | null;
  finished_at?: string | null;
  error?: string | null;
  /** Live progress of a page-by-page step (the LLM): pages finished out of all; 0 / 0 for other steps. */
  pages_done?: number;
  pages_total?: number;
  /** Partial failure of an otherwise successful step; technical English, not for display. */
  note?: string | null;
  /** Pages the step could not analyse (numbers as in `GET /documents/{id}/pages/{n}`), in page order. */
  skipped_pages?: number[];
}

export interface DetectionStatusModel {
  status: JobStatus;
  steps: StepModel[];
  started_at?: string | null;
  finished_at?: string | null;
  duration_seconds?: number | null;
  error?: string | null;
}

export interface CategorySummary {
  key: Category;
  label_cs: string;
  /** Hex colour and its light tint from the prototype palette. */
  color: string;
  tint: string;
  /** All findings in the category (a multi-category finding counts in each). */
  total: number;
  /** Findings that will take part in the export. */
  active: number;
  /** True when the whole category is excluded from the export. */
  excluded: boolean;
}

/** A statement declaring part of the document sensitive whose section could not be located. */
export interface DeclarationWarningModel {
  page: number;
  /** The declaring sentence as found on the page. */
  statement: string;
  /** The part of the document it names, as written; `null` when it names none. */
  reference: string | null;
}

export interface DocumentModel {
  id: string;
  filename: string;
  size_bytes: number;
  page_count: number;
  has_text_layer: boolean;
  /** A scan whose text is the scanner's OCR layer: detection and the text download work, PDF exports are refused (409). */
  scanned?: boolean;
  created_at: string;
  /** When the in-memory session is discarded unless there is activity. */
  expires_at: string;
  detection?: DetectionStatusModel | null;
  /** The session's category snapshot; empty until the first detection run starts. */
  categories: CategorySummary[];
  declaration_warnings: DeclarationWarningModel[];
  active_findings: number;
  excluded_findings: number;
}

export interface CreateManualFinding {
  page: number;
  start: number;
  end: number;
  category: Category;
  /** Defaults to `OTHER` on the backend. */
  entity_type?: string;
  note?: string | null;
}

export interface UpdateFinding {
  status: FindingStatus;
}

export interface UpdateCategory {
  excluded: boolean;
}

/** The result of an export download: the PDF and the name the backend suggested. */
export interface ExportedPdf {
  blob: Blob;
  filename: string;
}
